import { Header, Paragraph, ResponsiveButton, TrashCanIcon } from "@datagame/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { useMutation, useQuery } from "@apollo/client";
import { LIBRARY_IMAGES_QUERY } from "../../../../graphql/queries/LibraryImageQueries";
import { EmptyList } from "../../../shared/EmptyList";
import { Table } from "../../../shared/ui/Table";
import { TableConfig } from "../../../../types/table";
import { LibraryImage } from "../../../../gql/graphql";
import { ImageCategoryBadge } from "../../../shared/ui/ImageCategoryBadge";
import { LIBRARY_IMAGE_DELETE } from "../../../../graphql/mutations/LibraryImageMutations";
import { WithQueryLoader } from "../../../shared/WithQueryLoader";

import styles from "./LibrarySection.module.scss";

const cx = classNames.bind(styles);

export const LibrarySection = () => {
  const { t } = useTranslation();
  const { data: libaryData, loading } = useQuery(LIBRARY_IMAGES_QUERY);
  const [deleteImage] = useMutation(LIBRARY_IMAGE_DELETE, { refetchQueries: [LIBRARY_IMAGES_QUERY] });

  const handleDelete = (id: string) => {
    deleteImage({ variables: { libraryImageId: id } });
  };

  const tableConfig = useMemo<TableConfig<LibraryImage>>(
    () => ({
      getRowClassName: () => styles.rowWrapper,
      columns: [
        {
          width: 300,
          header: t("library:librarySection.table.fileName"),
          render: ({ url, name }) => {
            return (
              <div className={styles.tableNameWrapper}>
                <img className={styles.tableImage} src={url} alt="libarary-item" />
                <Paragraph size="xs" weight="font-bold" className={styles.tableName}>
                  {name}
                </Paragraph>
              </div>
            );
          },
        },
        {
          width: 200,
          header: t("library:librarySection.table.category"),
          render: ({ kind }) => <ImageCategoryBadge kind={kind} />,
        },
        {
          width: 40,
          header: "",
          className: styles.deleteCell,
          render: ({ id }) => {
            return (
              <ResponsiveButton
                iconSide="left"
                kind="minimal"
                color="red"
                icon={<TrashCanIcon />}
                className={styles.deleteButton}
                onClick={() => handleDelete(id)}
              >
                {t("library:librarySection.table.delete")}
              </ResponsiveButton>
            );
          },
        },
      ],
    }),
    [],
  );

  const renderTable = () => {
    if (!libaryData || libaryData.libraryImageAll.length === 0) {
      return <EmptyList title={t("library:emptyList")} description={t("library:emptyListDescription")} />;
    }

    return <Table data={libaryData.libraryImageAll as LibraryImage[]} config={tableConfig} />;
  };

  return (
    <WithQueryLoader isLoading={loading}>
      <div className={cx("librarySection", { empty: !libaryData || libaryData.libraryImageAll.length === 0 })}>
        <div className={styles.headerWrapper}>
          <Header variant="h6">{t("library:librarySection.header")}</Header>
          <Paragraph color="secondary" size="small">
            {t("library:librarySection.description")}
          </Paragraph>
        </div>
        {renderTable()}
      </div>
    </WithQueryLoader>
  );
};
